/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SortablePanels } from '@opengeoweb/shared';
import type {
  ResizablePanelComponent,
  SortablePanelItem,
} from '@opengeoweb/shared';
import {
  TimeSeriesPanelConnect,
  TIME_SERIES_PANEL_MIN_HEIGHT,
} from './TimeSeriesPanelConnect';
import { timeSeriesActions } from '../../store/reducer';
import { getPlotState } from '../../store/selectors';
import { TimeSeriesActionOrigin } from '../../store/types';
import type { TimeSeriesModuleState } from '../../store/types';
import type { Plot } from '../TimeSeries/types';

export interface TimeSeriesPanelsProps {
  viewId: string;
  panelComponent?: ResizablePanelComponent;
  renderPlot: (plotId: string) => React.ReactNode;
}

export const TimeSeriesPanelsConnect = ({
  viewId,
  panelComponent = TimeSeriesPanelConnect,
  renderPlot,
}: TimeSeriesPanelsProps): React.ReactElement => {
  const dispatch = useDispatch();

  const panelsInOrder: Plot[] = useSelector(
    (store: TimeSeriesModuleState) => getPlotState(store, viewId)?.plots || [],
  );

  const handleOrderChange = (
    newOrder: { id: string; name: string; isDisplayed: boolean }[],
  ): void => {
    const visiblePlots = panelsInOrder.filter((plot) => plot.enabled !== false);
    if (
      newOrder.length === visiblePlots.length &&
      newOrder.every(({ id }, index) => id === visiblePlots[index].plotId)
    ) {
      return;
    }
    dispatch(
      timeSeriesActions.reorderPlots({
        panelId: viewId,
        origin: TimeSeriesActionOrigin.timeSeriesManager,
        newOrder: newOrder.map(({ id }) => id),
      }),
    );
  };

  const panels = panelsInOrder
    .filter((plot) => plot.enabled !== false)
    .map(
      ({ plotId: id, title: name, isOpen, customSize }): SortablePanelItem => {
        return {
          id,
          name,
          isDisplayed: true,
          isOpen: isOpen !== false,
          customSize,
          minWidth: TIME_SERIES_PANEL_MIN_HEIGHT,
          render: (): React.ReactNode => renderPlot(id),
        };
      },
    );

  return (
    <SortablePanels
      allowOverflow
      direction="vertical"
      key={viewId}
      onOrderChange={handleOrderChange}
      panelComponent={panelComponent}
      panels={panels}
      sizing="untilManualResize"
      viewId={viewId}
    />
  );
};
